import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Layers } from 'lucide-react';
import { fetchExhibitByUUID, fetchSimilarExhibitsByPriority } from '../services/Exhibit';

function ExhibitDetail() {
    const { uuid } = useParams();
    const [exhibit, setExhibit] = useState(null);
    const [additionalExhibits, setAdditionalExhibits] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadingError, setLoadingError] = useState(false);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const getExhibit = async () => {
            if (uuid) {
                try {
                    const data = await fetchExhibitByUUID(uuid);
                    if (data) {
                        setExhibit(data);
                        if (data.room) {
                            const additionalData = await fetchSimilarExhibitsByPriority(data.room, uuid, data.priority);
                            setAdditionalExhibits(additionalData);
                        }
                    } else {
                        setLoadingError(true);
                    }
                } catch (error) {
                    console.error('Failed to fetch exhibit:', error);
                    setLoadingError(true);
                }
            } else {
                setLoadingError(true);
            }
        };

        getExhibit();
    }, [uuid, i18n.language]);

    if (loadingError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                    <h2 className="text-2xl font-bold mb-4 text-dark-blue">{t('exhibitNotFound')}</h2>
                    <button 
                        onClick={() => navigate('/scan')} 
                        className="bg-light-yellow text-dark-blue py-2 px-6 rounded-lg shadow-md hover:bg-teal transition-colors"
                    >
                        {t('scanAnotherCode')}
                    </button>
                </div>
            </div>
        );
    }

    if (!exhibit) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                <div className="text-xl text-dark-blue">{t('loading')}</div>
            </div>
        );
    }

    const { name, image, audio, description, room, dating, material } = exhibit;

    const handlePrev = () => {
        setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : image.length - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex < image.length - 1 ? prevIndex + 1 : 0));
    };

    return (
        <div className="min-h-screen bg-teal bg-opacity-10 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg">
                <div className="relative p-6 sm:p-8">
                    <button
                        onClick={() => navigate('/exhibits')}
                        className="absolute top-4 right-4 p-2 rounded-full hover:bg-light-yellow hover:bg-opacity-20 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-6 w-6 text-dark-blue" />
                    </button>
                    
                    <h2 className="text-3xl sm:text-4xl font-bold text-dark-blue mb-2">{name || t('noName')}</h2>
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        {room && (
                            <div className="flex items-center gap-2 text-dark-blue">
                                <MapPin className="h-5 w-5" />
                                <span className="text-lg italic">{room}</span>
                            </div>
                        )}
                        {dating && (
                            <div className="flex items-center gap-2 text-dark-blue">
                                <Calendar className="h-5 w-5" />
                                <span className="text-lg italic">{dating}</span>
                            </div>
                        )}
                        {material && (
                            <div className="flex items-center gap-2 text-dark-blue">
                                <Layers className="h-5 w-5" />
                                <span className="text-lg italic">{material}</span>
                            </div>
                        )}
                    </div>

                    
                    {image && image.length > 0 && (
                        <div className="relative mb-8 bg-light-yellow bg-opacity-10 rounded-lg overflow-hidden border-2 border-light-yellow">
                            {image.length > 1 && (
                                <button
                                    onClick={handlePrev}
                                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-light-yellow p-2 rounded-full shadow-lg hover:bg-teal transition-colors z-10"
                                >
                                    <ChevronLeft className="h-6 w-6 text-dark-blue" />
                                </button>
                            )}
                            <div className="flex justify-center">
                                <img
                                    src={image[currentIndex]}
                                    alt={`${name || 'Exhibit'}${currentIndex + 1}`}
                                    className="w-full h-[400px] object-contain"
                                />
                            </div>
                            {image.length > 1 && (
                                <button
                                    onClick={handleNext}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-light-yellow p-2 rounded-full shadow-lg hover:bg-teal transition-colors z-10"
                                >
                                    <ChevronRight className="h-6 w-6 text-dark-blue" />
                                </button>
                            )}
                            {image.length > 1 && (
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                                    {image.map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentIndex(idx)}
                                            className={`w-2 h-2 rounded-full transition-colors ${
                                                idx === currentIndex ? 'bg-dark-blue' : 'bg-light-yellow'
                                            }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {audio && audio.length > 0 && (
                        <div className="mb-8 space-y-3">
                            {audio.map((aud, index) => (
                                <div key={index} className="bg-light-yellow bg-opacity-10 rounded-lg p-3 border-2 border-dark-blue">
                                    <audio controls className="w-full">
                                        <source src={aud} type="audio/mpeg" />
                                    </audio>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="prose max-w-none mb-8">
                        <p className="text-lg text-dark-blue whitespace-pre-line text-justify">
                            {description || t('noDescription')}
                        </p>
                    </div>

                    <div className="flex gap-4 mb-8">
                        <button
                            onClick={() => navigate('/exhibits')}
                            className="flex-1 sm:flex-none bg-light-yellow text-dark-blue py-2 px-6 rounded-lg hover:bg-teal transition-colors"
                        >
                            {t('backToList')}
                        </button>
                        <button
                            onClick={() => navigate('/scan')}
                            className="flex-1 sm:flex-none bg-dark-blue text-white py-2 px-6 rounded-lg hover:bg-teal transition-colors"
                        >
                            {t('scanAnotherCode')}
                        </button>
                    </div>

                    {additionalExhibits.length > 0 && (
                        <div>
                            <h3 className="text-2xl font-bold text-dark-blue mb-6">{t('moreExhibitsFromRoom')}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {additionalExhibits.map((exhibit) => (
                                    <div
                                        key={exhibit.id}
                                        onClick={() => navigate(`/exhibits/${exhibit.uuid}`)}
                                        className="bg-light-yellow bg-opacity-10 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all duration-200 border-2 border-dark-blue hover:border-light-yellow"
                                    >
                                        {exhibit.image && exhibit.image[0] && (
                                            <div className="aspect-square mb-3 rounded-lg overflow-hidden bg-white">
                                                <img
                                                    src={exhibit.image[0]}
                                                    alt={exhibit.name}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                        )}
                                        <h4 className="font-semibold text-dark-blue mb-2">{exhibit.name}</h4>
                                        <p className="text-sm text-dark-blue opacity-80 line-clamp-2 text-justify">
                                            {exhibit.description || t('noDescription')}
                                        </p>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/exhibits/${exhibit.uuid}`);
                                            }}
                                            className="mt-3 text-dark-blue hover:text-teal text-sm font-medium"
                                        >
                                            {t('viewExhibit')} →
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExhibitDetail;